import clsx from 'clsx'
import { computed, defineComponent } from 'vue'
import { RouterLink } from 'vue-router'

import VButton from '@/components/common/button/VButton'
import PromptModal from '@/components/common/modal/PromptModal'
import VChip from '@/components/common/VChip'
import VIcon from '@/components/common/VIcon'
import useAuth from '@/composables/auth/useAuth'
import useLocalization from '@/composables/localization/useLocalization'
import useBookingDetailsView from '@/composables/views/booking-details/useBookingDetailsView'
import { getDisplayPatientVip, getStatusBgColor } from '@/utils/booking'
import { dateTextFormat } from '@/utils/date'
import { getLocalizationFirstName, getLocalizationLastName } from '@/utils/localization'
import { isNil, isStrNil } from '@/utils/validation'

export default defineComponent(() => {
  const auth = useAuth()
  const { localeTexts, translationWithFragmentKeys } = useLocalization()
  const {
    bookingQuery,
    cancelBookingConfirmModal,
    patientFullName,
    visitDate,
    handleShowCancelBookingConfirmModal
  } = useBookingDetailsView()

  // #region Getters

  const statusClass = computed((): string | undefined => {
    return bookingQuery.data.value && getStatusBgColor(bookingQuery.data.value.booking_status)
  })

  const receiptNo = computed((): JSX.Element => {
    if (isNil(bookingQuery.data.value?.receipt_no)) {
      return <div class='text-body-1 text-gray-400'>{localeTexts.value['booking-details-receipt-no-no-data']}</div>
    }

    return <div class='text-heading-5 text-primary'>{bookingQuery.data.value.receipt_no}</div>
  })

  const organizationPoint = computed((): JSX.Element => {
    if (isNil(bookingQuery.data.value?.organization_point)) {
      return <div class='text-body-1 text-gray-400'>{localeTexts.value['booking-details-point-organization-point-no-data']}</div>
    }

    return <div class='text-heading-5 text-primary'>{bookingQuery.data.value.organization_point}</div>
  })

  const organizationExtraPoint = computed((): JSX.Element => {
    if (isNil(bookingQuery.data.value?.organization_extra_point)) {
      return <div class='text-body-1 text-gray-400'>{localeTexts.value['booking-details-point-organization-extra-no-data']}</div>
    }

    return <div class='text-heading-5 text-primary'>{bookingQuery.data.value.organization_extra_point}</div>
  })

  const agentPoint = computed((): JSX.Element => {
    if (isNil(bookingQuery.data.value?.agent_point)) {
      return <div class='text-body-1 text-gray-400'>{localeTexts.value['booking-details-point-agent-point-no-data']}</div>
    }

    return <div class='text-heading-5 text-primary'>{bookingQuery.data.value.agent_point}</div>
  })

  const agentExtraPoint = computed((): JSX.Element => {
    if (isNil(bookingQuery.data.value?.agent_extra_point)) {
      return <div class='text-body-1 text-gray-400'>{localeTexts.value['booking-details-point-agent-extra-no-data']}</div>
    }

    return <div class='text-heading-5 text-primary'>{bookingQuery.data.value.agent_extra_point}</div>
  })

  const doctor = computed((): string | null => {
    if (isNil(bookingQuery.data.value?.doctor)) {
      return '-'
    }

    return bookingQuery.data.value.doctor.name_th
  })

  const symptoms = computed((): string => {
    if (isStrNil(bookingQuery.data.value?.symptoms)) {
      return '-'
    }

    return bookingQuery.data.value.symptoms
  })

  const roomType = computed((): string | null => {
    if (isNil(bookingQuery.data.value?.room?.id)) {
      return '-'
    }

    return bookingQuery.data.value.room.name_en
  })

  const roomStartDate = computed((): string | undefined => {
    if (isNil(bookingQuery.data.value?.room_start_date)) {
      return ''
    }

    return dateTextFormat(bookingQuery.data.value.room_start_date)
  })

  const roomEndDate = computed((): string | undefined => {
    if (isNil(bookingQuery.data.value?.room_end_date)) {
      return ''
    }

    return dateTextFormat(bookingQuery.data.value.room_end_date)
  })

  const patientVip = computed((): string => {
    if (isNil(bookingQuery.data.value?.is_patient_vip)) {
      return '-'
    }

    return getDisplayPatientVip(bookingQuery.data.value.is_patient_vip)
  })

  const patientEmail = computed((): string => {
    if (isNil(bookingQuery.data.value?.patient_email)) {
      return '-'
    }

    return bookingQuery.data.value.patient_email
  })

  const insuranceCompany = computed((): string => {
    if (isStrNil(bookingQuery.data.value?.insurance_company)) {
      return '-'
    }

    return bookingQuery.data.value.insurance_company
  })

  const policyNumber = computed((): string => {
    if (isNil(bookingQuery.data.value?.policy_number)) {
      return '-'
    }

    return bookingQuery.data.value?.policy_number
  })

  const isCancelBtnActive = computed((): boolean => {
    if (!bookingQuery.data.value) {
      return false
    }

    return bookingQuery.data.value?.booking_status === 'Pending' || bookingQuery.data.value?.booking_status === 'Approved'
  })

  const bookingDetailsCancelModalParagraph = computed((): string => {
    return translationWithFragmentKeys(
      'booking-details-cancel-paragraph',
      [
        { fragment_key: 'patient', value: patientFullName.value },
        { fragment_key: 'visited-date', value: visitDate.value }
      ])
  })

  const createdByName = computed((): string => {
    if (!bookingQuery.data.value) {
      return ''
    }

    return `${getLocalizationFirstName(bookingQuery.data.value?.created_by, auth.userProfile!.language_preference)} ${getLocalizationLastName(bookingQuery.data.value?.created_by, auth.userProfile!.language_preference)}`
  })

  // #endregion

  bookingQuery.execute()

  return () => (
    <div class='px-4 pb-25 tablet:px-12 desktop:px-16'>
      <div class='py-2.5'>
        <RouterLink
          to='/'
          class='inline-flex space-x-2 text-neutral-700'
        >
          <VIcon
            class='text-[24px]'
            icon='material-symbols:keyboard-backspace'
          />
          <span class='text-button-2 uppercase'>{localeTexts.value['booking-details-back-btn']}</span>
        </RouterLink>
      </div>
      <h1 class='text-heading-5'>{localeTexts.value['booking-details-heading']}</h1>
      <div class='mt-4 flex items-center'>
        <div class='flex flex-col items-start space-x-0 space-y-2 tablet:flex-1 tablet:flex-row tablet:items-center tablet:space-x-6 tablet:space-y-0'>
          <div
            t-data='booking-id'
            class='text-subtitle-1'
          >{localeTexts.value['booking-details-booking-id-title']} {bookingQuery.data.value?.booking_no}
          </div>
          <div class='flex items-center space-x-2'>
            <div class='text-subtitle-1'>{localeTexts.value['booking-details-status-title']}</div>
            <VChip
              t-data='booking-status'
              class={statusClass.value}
            >
              {bookingQuery.data.value?.booking_status}
            </VChip>
          </div>
        </div>
        <div class='hidden flex-none space-x-4 tablet:flex'>
          {isCancelBtnActive.value &&
            <VButton
              t-data='booking-cancel-btn'
              class='btn-medium'
              color={clsx('btn-gray')}
              onClick={handleShowCancelBookingConfirmModal}
            >{localeTexts.value['booking-details-cancel-btn']}
            </VButton>}
        </div>
      </div>
      <div class='mt-4 grid grid-cols-1 divide-x-0 divide-y divide-neutral-300 rounded border border-neutral-300 bg-neutral-50 tablet:grid-cols-3 tablet:divide-x tablet:divide-y-0'>
        <div
          t-data='booking-receipt-no'
          class='flex flex-col items-start px-4 py-2'
        >
          <div class='text-overline uppercase text-neutral-700'>{localeTexts.value['booking-details-receipt-no-title']}</div>
          <div>{receiptNo.value}</div>
        </div>
        <div
          t-data='booking-agent-point'
          class='flex flex-col items-start px-4 py-2'
        >
          <div class='text-overline uppercase text-neutral-500'>{localeTexts.value['booking-details-point-agent-title']}</div>
          <div class='grid grid-cols-2 gap-x-8 tablet:grid-cols-1 tablet:gap-y-2 desktop:grid-cols-2 desktop:gap-x-20'>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1 text-neutral-700'>{localeTexts.value['booking-details-point-agent-point-subtitle']}</div>
              {agentPoint.value}
            </div>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1 text-neutral-700'>{localeTexts.value['booking-details-point-agent-extra-subtitle']}</div>
              {agentExtraPoint.value}
            </div>
          </div>
        </div>
        <div
          t-data='booking-organization-point'
          class='flex flex-col items-start px-4 py-2'
        >
          <div class='text-overline uppercase text-neutral-500'>{localeTexts.value['booking-details-point-organization-title']}</div>
          <div class='grid grid-cols-2 gap-x-8 tablet:grid-cols-1 tablet:gap-y-2 desktop:grid-cols-2 desktop:gap-x-20'>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1 text-neutral-700'>{localeTexts.value['booking-details-point-organization-point-subtitle']}</div>
              {organizationPoint.value}
            </div>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1 text-neutral-700'>{localeTexts.value['booking-details-point-organization-extra-subtitle']}</div>
              {organizationExtraPoint.value}
            </div>
          </div>
        </div>
      </div>
      <div class='mt-4 divide-y divide-neutral-300 rounded border border-neutral-300'>
        <div class='p-4'>
          <div class='text-subtitle-1 text-neutral-500'>{localeTexts.value['booking-details-visit-details-heading']}</div>
          <div class='mt-4 space-y-2'>
            <div class='flex items-start space-x-2 tablet:items-center'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-service-label']}</div>
              <div t-data='booking-service'>{bookingQuery.data.value?.service.name_en}</div>
            </div>
            <div class='flex items-start space-x-2 tablet:items-center'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-datetime-label']}</div>
              <div t-data='booking-datetime'>{visitDate.value}, {bookingQuery.data.value?.visit_time_string}</div>
            </div>
            <div class='flex items-start space-x-2 tablet:items-center'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-symptoms-label']}</div>
              <div t-data='booking-symptoms'>{symptoms.value}</div>
            </div>
            <div class='flex items-start space-x-2 tablet:items-center'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-doctor-label']}</div>
              <div t-data='booking-doctor'>{doctor.value}</div>
            </div>
            <div class='flex items-start space-x-2 tablet:items-center'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-room-type-label']}</div>
              <div t-data='booking-room-type'>{roomType.value}</div>
            </div>
            <div class='flex items-start space-x-2 tablet:items-center'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-room-stay-date-label']}</div>
              <div t-data='booking-room-stay-date'>{roomStartDate.value} - {roomEndDate.value}</div>
            </div>
          </div>
        </div>
        <div class='p-4'>
          <div class='text-subtitle-1 text-neutral-500'>{localeTexts.value['booking-details-patient-details-heading']}</div>
          <div class='mt-4 space-y-2'>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-patient-name-label']}</div>
              <div t-data='booking-patient-name'>{patientFullName.value}</div>
            </div>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-patient-vip-label']}</div>
              <div t-data='booking-patient-vip'>{patientVip.value}</div>
            </div>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-patient-email-label']}</div>
              <div t-data='booking-patient-email'>{patientEmail.value}</div>
            </div>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-patient-mobile-label']}</div>
              <div t-data='booking-patient-mobile'>{bookingQuery.data.value?.patient_phone}</div>
            </div>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-patient-organization-label']}</div>
              <div t-data='booking-patient-insurance'>{insuranceCompany.value}</div>
            </div>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-patient-policy-number-label']}</div>
              <div t-data='booking-patient-policy-number'>{policyNumber.value}</div>
            </div>
          </div>
        </div>
        <div class='p-4'>
          <div class='text-subtitle-1 text-neutral-500'>{localeTexts.value['booking-details-agent-information-heading']}</div>
          <div class='mt-4 space-y-2'>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-agent-name-label']}</div>
              <div t-data='booking-agent-name'>{createdByName.value}</div>
            </div>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-agent-organization-label']}</div>
              <div t-data='booking-agent-organization'>{bookingQuery.data.value?.created_by.organization?.name}</div>
            </div>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-agent-email-label']}</div>
              <div t-data='booking-agent-email'>{bookingQuery.data.value?.created_by.email}</div>
            </div>
            <div class='flex items-center space-x-2'>
              <div class='text-subtitle-1'>{localeTexts.value['booking-details-agent-mobile-label']}</div>
              <div t-data='booking-agent-mobile'>{bookingQuery.data.value?.created_by.mobile}</div>
            </div>
          </div>
        </div>
      </div>
      <div class='mt-4 flex items-center justify-end tablet:hidden'>
        <div class='flex space-x-4'>
          {isCancelBtnActive.value &&
            <VButton
              t-data='booking-cancel-btn'
              class='btn-medium'
              color={clsx('btn-gray')}
              onClick={handleShowCancelBookingConfirmModal}
            >{localeTexts.value['booking-details-cancel-btn']}
            </VButton>}
        </div>
      </div>
      {cancelBookingConfirmModal.isRevealed.value &&
        <PromptModal
          t-data='booking-cancel-confirm-modal'
          modalkey={cancelBookingConfirmModal.key}
          placeholder={localeTexts.value['booking-details-cancel-reason-placeholder']}
          title={localeTexts.value['booking-details-cancel-title']}
          content={bookingDetailsCancelModalParagraph.value}
          confirmText={localeTexts.value['booking-details-cancel-confirm-btn']}
          confirmColor={clsx('btn-danger')}
        />}
    </div>
  )
}, {
  name: 'BookingDetailsView'
})
